import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import ReceiptIcon from "@material-ui/icons/Receipt";
import CloseIcon from "@material-ui/icons/Close";
import dayjs, { Dayjs } from "dayjs";
import DateRangePicker from "../../../../components/inputs/daterangepickerv2";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import MaterialTable from "@material-table/core";
import useStyles from "../../styles";
import {
  MeetingRoomOutlined,
  CreditScoreOutlined,
  ChevronRight
} from "@mui/icons-material";
import { UserEventType } from "../../../../constants";
import { splitCamelCase } from "../../../../utils";
import { Currency } from "../../../../components/misc/Currency";

import * as StoreApi from "../../../../api/storeApi";
import { useSelector } from "react-redux";
import { AppState } from "../../../../store";
import { StoresState } from "../../../../store/stores/types";
import { UserOverview } from "../../../users/UserOverview";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import { useI18n } from "../../../../hooks/useI18n";

const fetchEvents = async (
  storeId: string,
  startDate: string,
  endDate: string
) => {
  return await StoreApi.listEvents(storeId, startDate, endDate);
};

interface StoreUserEventsDialogProps {
  open: boolean;
  onClose: () => void;
}

const StoreUserEventsDialog: React.FC<StoreUserEventsDialogProps> = ({
  open,
  onClose
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmallDown = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useI18n();

  const { stores } = useSelector<AppState, StoresState>(s => s.stores);

  const [startDate, setStartDate] = useState<Dayjs>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());

  const [userId, setUserId] = useState("");

  const handleDateRangeChange = ([start, end]: [Dayjs, Dayjs]) => {
    setStartDate(start);
    setEndDate(end);
  };

  const formattedStartDate = startDate.toISOString();
  const formattedEndDate = endDate.toISOString();

  const { data, isLoading, isError } = useQuery(
    ["receipts", formattedStartDate, formattedEndDate],
    () => fetchEvents(stores[0].id, formattedStartDate, formattedEndDate),
    {
      staleTime: Infinity,
      enabled: !!formattedStartDate && !!formattedEndDate
    }
  );

  const renderEvent = (row: any) => {
    let eventRepr = row.event;
    if (row.event === UserEventType.OPEN_DOOR) {
      eventRepr = (
        <MeetingRoomOutlined
          style={{
            opacity: !row.successful ? 0.5 : ""
          }}
        />
      );
    } else if (row.event === UserEventType.PAYMENT) {
      eventRepr = (
        <CreditScoreOutlined
          style={{
            color: theme.palette.success.dark,
            opacity: !row.successful ? 0.5 : ""
          }}
        />
      );
    }

    return (
      <Box display={"flex"} alignItems={"center"}>
        <Box pr={2}>{eventRepr}</Box>
        {!row.successful && !isSmallDown && (
          <Typography
            variant={"subtitle2"}
            style={{ color: theme.palette.error.dark }}
          >
            Failed
          </Typography>
        )}
      </Box>
    );
  };

  const renderEventDetailPanel = (rowData: any) => {
    return {
      icon: () =>
        rowData.event === UserEventType.PAYMENT ||
        rowData.dynamicData.failureReason ? (
          <ChevronRight fontSize={"medium"} />
        ) : (
          <ChevronRight style={{ fontSize: 0 }} />
        ),
      render: (rowData: any) => {
        return (
          <Box
            px={2}
            py={2}
            display={"flex"}
            justifyContent={"center"}
            className={classes.eventDetailTableContainer}
          >
            <Box width={"100%"}>
              {rowData.dynamicData.paymentMethod && (
                <Box style={{ textTransform: "uppercase" }} pl={4}>
                  <Typography variant={"caption"} color={"textSecondary"}>
                    {splitCamelCase(rowData.dynamicData.paymentMethod)}
                  </Typography>
                </Box>
              )}
              {rowData.successful ? (
                <Box
                  justifyContent={"center"}
                  flexDirection={"row"}
                  display={"flex"}
                >
                  <table className={classes.eventDetailTable}>
                    <thead>
                      <tr>
                        <th>{t("common.tableHeaders.receiptNumber")}</th>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rowData.dynamicData.receiptItems.map((ri: any) => {
                        return (
                          <tr key={ri.receiptItemId}>
                            <td>{rowData.dynamicData.receiptId}</td>
                            <td>{ri.productName}</td>
                            <td>{ri.quantity}</td>
                            <td>
                              <Currency value={ri.receiptItemPrice} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              ) : (
                <Box>
                  {rowData.dynamicData.failureReason && (
                    <Box pl={4}>
                      <Typography variant={"body2"} component={"span"}>
                        <b style={{ color: "#424242" }}>
                          {t("common.formFields.failureReason")}
                        </b>
                        : {splitCamelCase(rowData.dynamicData.failureReason)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        );
      }
    };
  };

  return (
    <>
      <Dialog
        fullWidth
        fullScreen={isSmallDown}
        maxWidth="md"
        open={open}
        onClose={onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <div className={classes.dialogHeader}>
          <SupervisedUserCircleIcon className={classes.receiptIcon} />
          <Typography
            variant="button"
            component="span"
            style={{ fontWeight: 500, marginRight: 16 }}
          >
            User event for period
          </Typography>
          <div className={classes.datePickerWrapper}>
            <DateRangePicker onDateRangeChange={handleDateRangeChange} />
          </div>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent dividers>
          <MaterialTable
            style={{ boxShadow: "none" }}
            title=""
            columns={[
              {
                title: "Timestamp",
                field: "timestamp",
                cellStyle: { padding: isSmallDown ? "8px" : "" },
                render: rowData =>
                  moment(rowData.timestamp).format("YYYY-MM-DD HH:mm")
              },
              {
                title: "Event",
                field: "event",
                render: (row: any) => renderEvent(row)
              },
              {
                title: "Status",
                field: "successful",
                export: true,
                hidden: true
              },
              {
                title: t("common.tableHeaders.userId"),
                field: "user.id",
                hidden: true,
                export: true
              },
              {
                title: t("common.tableHeaders.firstName"),
                field: "user.firstName",
                hidden: true,
                export: true
              },
              {
                title: t("common.tableHeaders.lastName"),
                field: "user.lastName",
                hidden: true,
                export: true
              },
              {
                title: "User",
                field: "user.firstName",
                export: false,
                cellStyle: { padding: isSmallDown ? "8px" : "" },
                render: (rowData: any) => (
                  <Link
                    component={"button"}
                    onClick={() => setUserId(rowData.user.id)}
                  >
                    {`${rowData.user.firstName} ${rowData.user.lastName}`}
                  </Link>
                )
              },
              {
                title: t("common.tableHeaders.receiptNr"),
                field: "dynamicData.receiptId",
                hidden: true,
                export: true,
                searchable: true
              },
              {
                title: "Products & Price",
                field: "dynamicData.receiptItems",
                hidden: true,
                export: true
              },
              {
                title: t("common.tableHeaders.paymentMethod"),
                field: "dynamicData.paymentMethod",
                hidden: true,
                export: true
              },
              {
                title: t("common.tableHeaders.failureReason"),
                field: "dynamicData.failureReason",
                hidden: true,
                export: true
              }
            ]}
            data={
              data && data.length
                ? data.map((event: any) => ({
                    ...event
                  }))
                : []
            }
            isLoading={isLoading}
            detailPanel={[rowData => renderEventDetailPanel(rowData)]}
            options={{
              rowStyle: row => ({
                color: !row.successful ? "rgba(0,0,0,0.4)" : "",
                backgroundColor: !row.successful ? "rgb(253, 236, 234)" : ""
              }),
              pageSize: 10,
              headerStyle: {
                padding: isSmallDown ? "8px" : "1rem"
              }
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        onClose={() => setUserId("")}
        open={userId !== ""}
        fullWidth={true}
        fullScreen={isSmallDown}
        maxWidth={"xl"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle style={{ height: 50 }} disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => setUserId("")}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent style={isSmallDown ? { padding: 0 } : {}}>
          <UserOverview inputUserId={userId} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StoreUserEventsDialog;
